import React, { useContext, useEffect, useState } from 'react';
import { Modal, Button, Nav, Tab, Form } from 'react-bootstrap';
import ComboBox, { ComboBoxMode, Option } from './Combobox';
import Checkbox from './Checkbox';
import LabelButton from './LabelButton';
import { LanguageContext } from '../lib/i18n/LanguageProvider';
import DataTable from 'react-data-table-component'; // DataTable bileşeni
import Divider from './Divider';
import TextBox, { InputType } from './Textbox';
import DateTimePicker from './DateTimePicker';
import { QRCode } from 'react-qrcode-logo';
import LoadingButton from './Button';




// Element types
export enum ElementType {
  Input = 'input',
  LoadingButton = 'loadingButton',
  LabelButton = 'labelButton',
  ComboBox = 'comboBox',
  TextArea = 'textArea',
  TextBox = 'textBox',
  Label = 'label',
  TextLabel = 'textLabel',
  Checkbox = 'checkbox',
  CheckboxWithLink = 'checkboxWithLink',
  Image = 'image',
  PDF = 'pdf',
  QRCode = 'qr_code',
  DateTimePicker = 'dateTimePicker',
  DataTable = 'table',
  Container = 'container',
  Divider = 'divider',
}

export enum SizeType {
  sm = 'sm',
  lg = 'lg',
  xl = 'xl',
}

export interface Element {
  id?: string;
  type: ElementType;
  label?: string;
  value?: string;
  required?: boolean;
  disabled?: boolean;
  onChange?: (value: boolean | string) => void;
  onClick?: () => void;
  checked?: boolean;
  url?: string;
  download?: string;
  src?: string;
  alt?: string;
  style?: React.CSSProperties;
  qrLink?: string;
  qrSize?: number;
  options?: Option[];
  mode?: ComboBoxMode;
  className?: string;
  searchable?: boolean;
  visible?: boolean;
  useHTML?: boolean; // <- NEW: useHTML property


  // DateTimePicker özellikleri
  showTime?: boolean;
  showOnlyTime?: boolean;
  errorMessage?: string;

  tableColumns?: {
    name: string;
    selector: (row: any) => any;
    sortable?: boolean;
  }[];  // DataTable için sütun tanımları
  tableData?: any[];  // DataTable için veri
  elements?: Element[];

  // TextBox bileşeni için özel props
  buttonIcon?: React.ReactNode;
  buttonOnClick?: () => void;
  buttonTooltip?: string;
  placeholder?: string;
  inputType?: InputType;
}

export interface TabContent {
  tabName: string;
  elements: Element[];
}

interface CustomModalProps {
  size?: SizeType;
  show: boolean;
  onHide: () => void;
  title: string;
  content: TabContent[];
  confirmMode?: boolean;
  confirmMessage?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  confirmButtonText?: string; // Onay butonu metni
  footerActionButtonText?: string; // Yeni aksiyon butonu metni
  onActionButtonClick?: () => void; // Yeni aksiyon butonu tıklama olayı
}

const CustomModal: React.FC<CustomModalProps> = ({
  size = SizeType.lg,
  show,
  onHide,
  title,
  content,
  confirmMode = false,
  confirmMessage,
  onConfirm,
  onCancel,
  confirmButtonText,
  footerActionButtonText, // Yeni buton metni
  onActionButtonClick, // Yeni buton tıklama olayı
}) => {
  const [hasTabs, setHasTabs] = useState(false);
  const [activeTab, setActiveTab] = useState(content[0]?.tabName || '');

  useEffect(() => {
    if (content && content.length > 1) {
      setHasTabs(true);
      setActiveTab(content[0]?.tabName || '');
    } else {
      setHasTabs(false);
    }
  }, [content]);

  const renderElement = (element: Element, index: number) => {
    const elementClassName = element.className || 'col-12';

    if (element.visible === false) {
      return null;
    }

    switch (element.type) {
      case ElementType.Container: // Yeni container tipi için case
        return (
          <div key={index} className={element.className}>
            {element.elements && element.elements.map((childElement, childIndex) =>
              renderElement(childElement, childIndex) // İç elementleri tekrar renderElement ile render ediyoruz
            )}
          </div>
        );
      case ElementType.Divider:
        return (
          <Divider
            key={index}
            thickness={typeof element.style?.height === 'number' ? `${element.style.height}px` : element.style?.height || '1px'}
            margin={typeof element.style?.margin === 'number' ? `${element.style.margin}px` : element.style?.margin || '20px 0'}
          />
        );
      case ElementType.Input:
        return (
          <Form.Group key={index} className={elementClassName}>
            {element.label && <Form.Label>{element.label}</Form.Label>}
            <Form.Control
              type="text"
              value={element.value}
              onChange={(e) => element.onChange && element.onChange(e.target.value)}
              required={element.required}
              disabled={element.disabled}
            />
          </Form.Group>
        );
      case ElementType.TextBox:
        return (
          <Form.Group key={index} className={elementClassName}>
            <TextBox
              label={element.label || ''}
              inputId={element.id || `text-box-${index}`}
              required={element.required}
              errorMessage={element.errorMessage}
              placeholder={element.placeholder}
              type={element.inputType || InputType.TEXT}
              value={element.value}
              onChange={(e) => element.onChange && element.onChange(e.target.value)}
            />
          </Form.Group>
        );

      case ElementType.Label:
        return (
          <Form.Group key={index} className={elementClassName}>
            {element.url ? (
              <a
                href={element.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'black', textDecoration: 'underline' }}
              >
                {element.label}
              </a>
            ) : (
              <Form.Label>{element.label}</Form.Label>
            )}
          </Form.Group>
        );
      case ElementType.TextLabel:
        return (
          <Form.Group key={index} className={elementClassName}>
            {element.url ? (
              <a
                href={element.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'black', textDecoration: 'underline' }}
              >
                {element.label}
              </a>
            ) : (
              <Form.Label>{element.label}</Form.Label>
            )}
          </Form.Group>
        );
      case ElementType.TextArea:
        return (
          <Form.Group key={index} className={elementClassName}>
            {element.useHTML ? (
              <div dangerouslySetInnerHTML={{ __html: element.value || '' }} />
            ) : (
              <Form.Control
                as="textarea"
                value={element.value}
                onChange={(e) => element.onChange && element.onChange(e.target.value)}
                required={element.required}
                disabled={element.disabled}
              />
            )}
          </Form.Group>
        );
      case ElementType.Checkbox:
        return (
          <Form.Group key={index} className={elementClassName}>
            <Checkbox
              label={element.label || ''}
              checkboxId={`checkbox-${index}`}
              checked={element.checked || false}
              onChange={(checked) => element.onChange && element.onChange(checked)}
            />
          </Form.Group>
        );
      case ElementType.CheckboxWithLink:
        return (
          <Form.Group key={index} className={elementClassName}>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                label={''}
                checkboxId={`checkbox-with-link-${index}`}
                checked={element.checked || false}
                required={element.required}
                disabled={element.disabled}
                onChange={(checked) => element.onChange && element.onChange(checked)}
              />
              <a
                href={element.url || '#'}
                target="_blank"
                rel="noopener noreferrer"
                style={{ marginBottom: '1rem', marginLeft: '10px', color: 'black', textDecoration: 'underline' }}
              >
                {element.label}
              </a>
            </div>
          </Form.Group>
        );
      case ElementType.LoadingButton:
        return (
          <Form.Group key={index} className={elementClassName}>
            <Button variant="primary" onClick={element.onClick} disabled={element.disabled}>
              {element.label}
            </Button>
          </Form.Group>
        );
      case ElementType.LabelButton:
        return (
          <Form.Group key={index} className={elementClassName}>
            <LabelButton
              label={element.label || ''}
              onClick={element.onClick || (() => { })}
              disabled={element.disabled}
              style={{ color: 'black', textDecoration: 'underline' }}

            />
          </Form.Group>
        );
      case ElementType.Image:
        return (
          <Form.Group key={index} className={elementClassName}>
            <div className="form-group">
              <img
                src={element.src}
                alt={element.alt || ''}
                style={element.style || { width: '150px', height: '150px' }}
              />
            </div>
          </Form.Group>
        );
      case ElementType.PDF:
        return (
          <Form.Group key={index} className={elementClassName}>
            <div className="form-group">
              <iframe
                src={element.src}
                style={element.style || { width: '100%', height: '500px' }}
                title={element.label || 'PDF'}
              ></iframe>
              {element.download && (
                <a
                  href={element.src}
                  download={element.download}
                  style={{ display: 'block', marginTop: '10px', color: 'black', textDecoration: 'underline' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = 'red')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'black')}
                >
                  {element.label || 'Download PDF'}
                </a>
              )}
            </div>
          </Form.Group>
        );
      case ElementType.ComboBox:
        return (
          <Form.Group key={index} className={elementClassName}>
            <ComboBox
              label={element.label || ''}
              inputId={element.id || 'combo-box'}
              options={element.options || []}
              value={element.value || ''}
              onChange={(value: string | string[]) => {
                const newValue = Array.isArray(value) ? value.join(', ') : value;
                if (element.onChange) {
                  element.onChange(newValue);
                }
              }}
              mode={element.mode || 1}
              disabled={element.disabled || false}
              searchable={element.searchable}
            />
          </Form.Group>
        );
      case ElementType.DateTimePicker:
        return (
          <Form.Group key={index} className={elementClassName}>
            <DateTimePicker
              label={element.label || ''}
              inputId={element.id || `date-time-picker-${index}`}
              value={element.value || ''}
              onChange={(value: string) => element.onChange && element.onChange(value)}
              showTime={element.showTime || false}
              showOnlyTime={element.showOnlyTime || false}
              required={element.required || false}
              errorMessage={element.errorMessage}
              disabled={element.disabled}
            />
          </Form.Group>
        );
      case ElementType.QRCode:
        return (
          <Form.Group key={index} className={elementClassName}>
            <div className="form-group">
              <QRCode id={element.id} value={element.qrLink} size={element.qrSize || 256} />
            </div>
          </Form.Group>
        );
      default:
        return null;
    }
  };

  const renderContentWithoutTabs = () => (
    <Form>
      <div className="row">
        {content[0]?.elements.map((element, index) => renderElement(element, index))}
      </div>
    </Form>
  );

  const renderContentWithTabs = () => (
    <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || '')}>
      <Nav variant="tabs">
        {content.map((tabContent, index) => (
          <Nav.Item key={index}>
            <Nav.Link eventKey={tabContent.tabName}>{tabContent.tabName}</Nav.Link>
          </Nav.Item>
        ))}
      </Nav>
      <hr className="tab-separator" />
      <Tab.Content>
        {content.map((tabContent, index) => (
          <Tab.Pane eventKey={tabContent.tabName} key={index}>
            <div className='row'>
              {tabContent.elements.map((element, idx) => renderElement(element, idx))}
            </div>
          </Tab.Pane>
        ))}
      </Tab.Content>
    </Tab.Container>
  );

  const renderConfirmMode = () => (
    <div>
      <p>{confirmMessage || 'Do you confirm this action?'}</p>
    </div>
  );

  return (
    <Modal show={show} onHide={onHide} size={size}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {confirmMode
          ? renderConfirmMode()
          : hasTabs
            ? renderContentWithTabs()
            : renderContentWithoutTabs()}
      </Modal.Body>
      <Modal.Footer>
        {confirmMode ? (
          <>
            <Button variant="primary" onClick={onConfirm}>
              {confirmButtonText || "Onayla"}
            </Button>
            <Button variant="secondary" onClick={onCancel || onHide}>
              İptal
            </Button>
          </>
        ) : (
          <>
            {footerActionButtonText && onActionButtonClick && (
              <LoadingButton
                className='btn btn-primary'
                label={footerActionButtonText}
                onClick={onActionButtonClick}
              />
              // <Button variant="primary" onClick={onActionButtonClick}>
              //   {footerActionButtonText}
              // </Button>
            )}
            <Button variant="secondary" onClick={onCancel || onHide}>
              Kapat
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default CustomModal;
